import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/hooks/useSEO";
import { toast } from "sonner";

/**
 * Partner login page. Intentionally low-key.
 * Not linked from public marketing (nav / footer / sitemap all exclude this).
 * Used by internal team & pre-approved partners who have the URL.
 */
export default function Partner() {
  const { user, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Partner Login | FlipPilot",
    description: "Team &amp; partner sign-in for FlipPilot.",
    path: "/partner",
  });

  useEffect(() => {
    if (!authLoading && user) navigate("/dashboard");
  }, [user, authLoading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="py-24" data-testid="partner-page">
      <div className="mkt-container max-w-md mx-auto">
        <div className="mkt-card p-8" data-testid="partner-login-card">
          <div
            className="w-11 h-11 grid place-items-center rounded-full mb-5"
            style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}
          >
            <Lock size={18} strokeWidth={2.2} />
          </div>
          <h1 className="text-2xl font-bold leading-none">Partner Sign-In</h1>
          <p className="mt-2 text-sm text-[var(--mkt-text-muted)]">
            For approved team members and partners.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[var(--mkt-text-muted)] mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="partner-email"
                className="w-full border border-[var(--mkt-line)] rounded-lg px-3 py-2.5 focus:outline-none focus:border-[var(--mkt-accent)] transition-colors"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-[var(--mkt-text-muted)] mb-2">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="partner-password"
                className="w-full border border-[var(--mkt-line)] rounded-lg px-3 py-2.5 focus:outline-none focus:border-[var(--mkt-accent)] transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="partner-submit"
              className="mkt-btn mkt-btn-primary w-full justify-center"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[var(--mkt-line)] text-xs text-[var(--mkt-text-muted)] text-center">
            Access is by invitation only.{" "}
            <Link to="/request-access"
                  data-testid="partner-request-access-link"
                  className="text-[var(--mkt-accent-dark)] font-semibold hover:underline">
              Request access · Join waiting list
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
